import type { AccessTier, Organization } from './api'

export type { AccessTier }

const PAID_PLANS = ['pro', 'team', 'business']

const isTier = (v: unknown): v is AccessTier => v === 'paid' || v === 'free' || v === 'none'

// The server owns the flag. The fallbacks protect accounts during a rolling deploy:
// a build that sends `tier` but not `features_enabled`, and one that sends neither.
export function hasProductAccess(organization: Organization | null): boolean {
  if (!organization) return false
  if (typeof organization.features_enabled === 'boolean') return organization.features_enabled
  if (isTier(organization.tier)) return organization.tier !== 'none'
  return PAID_PLANS.includes(organization.plan ?? '')
}

/**
 * What the organization may use: `paid` (Pro, Team, Business), `free` (the free plan:
 * 5 monitors, one location) or `none` (no access yet, so the plan picker shows).
 * Builds that do not send `tier` map to `paid` when product access is on, else `none`.
 */
export function accessTier(organization: Organization | null): AccessTier {
  if (!organization) return 'none'
  if (isTier(organization.tier)) return organization.tier
  return hasProductAccess(organization) ? 'paid' : 'none'
}

/** On the free plan: the UI hides Pro-only options and names what Pro adds. */
export function isFreeTier(organization: Organization | null): boolean {
  return accessTier(organization) === 'free'
}

/**
 * A paid plan is live. Product access is not enough to tell: the free plan has access
 * and no subscription, so only a paid organization changes plan in billing, and only
 * a non-paid one goes through checkout.
 */
export function hasPaidPlan(organization: Organization | null): boolean {
  return accessTier(organization) === 'paid'
}

/** Checkout, cancel, change plan and portal need owner or admin (server enforces). */
export function canManageBilling(user: { role?: string | null } | null | undefined): boolean {
  return user?.role === 'owner' || user?.role === 'admin'
}

/** Server response when the org already has a live subscription. */
export function isAlreadySubscribed(err: unknown): boolean {
  return typeof err === 'object' && err !== null && (err as { status?: number }).status === 409
}

type ActivationPollingOptions = {
  attempts?: number
  delayMs?: number
  signal?: AbortSignal
}

export async function waitForSubscriptionActivation(
  refreshOrganization: () => Promise<Organization | null>,
  { attempts = 20, delayMs = 1500, signal }: ActivationPollingOptions = {},
): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (signal?.aborted) return false
    try {
      // Paid, not just "has access": a free organization has access before it pays.
      if (hasPaidPlan(await refreshOrganization())) return true
    } catch {
      // A webhook or the auth endpoint may still be settling. Keep polling.
    }
    if (attempt < attempts - 1) {
      await new Promise<void>((resolve) => setTimeout(resolve, delayMs))
    }
  }
  return false
}
